import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  LinearProgress,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Star as StarIcon,
  Diamond as DiamondIcon,
  EmojiEvents as CrownIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Event as EventIcon,
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  ManageAccounts as ManageIcon,
  Storage as StorageIcon,
} from '@mui/icons-material';
import type { PlanInfo, UsageStats, UsageAlert } from '../../../types/billing';
import { isUnlimited } from '../../../types/billing';
import { UsageIndicator } from '../../plan/UsageIndicator';
import { UpgradePrompt } from '../../plan/UpgradePrompt';
import stripeService from '../../../services/stripeService';

interface PlanInfoCardProps {
  currentPlan: PlanInfo;
  usage: {
    currentMonth: UsageStats;
    alerts: UsageAlert[];
  };
  onUpgrade?: () => void;
  onChangePlan?: () => void;
}

const PlanInfoCard: React.FC<PlanInfoCardProps> = ({
  currentPlan,
  usage,
}) => {
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  // プランアイコンの取得
  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'basic':
        return <StarIcon color="primary" />;
      case 'standard':
        return <StarIcon sx={{ color: '#4caf50' }} />;
      case 'premium':
        return <DiamondIcon color="secondary" />;
      case 'enterprise':
        return <CrownIcon sx={{ color: '#ff6b35' }} />;
      default:
        return <StarIcon />;
    }
  };

  // プランカラーの取得
  const getPlanColor = (planId: string) => {
    switch (planId) {
      case 'basic':
        return 'primary';
      case 'standard':
        return 'success';
      case 'premium':
        return 'secondary';
      case 'enterprise':
        return 'error';
      default:
        return 'primary';
    }
  };


  // 機能リストの表示
  const renderFeatures = () => {
    return currentPlan.features.slice(0, 5).map((feature) => (
      <ListItem key={feature.id} dense>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleIcon color="success" fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={feature.name}
          secondary={feature.description}
          primaryTypographyProps={{ variant: 'body2' }}
          secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
        />
      </ListItem>
    ));
  };

  // アクティブなアラートの表示
  const activeAlerts = usage.alerts.filter(alert => !alert.isRead);

  return (
    <Box>
      {/* メインプラン情報 */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              {getPlanIcon(currentPlan.id)}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {currentPlan.name}
              </Typography>
            </Box>
            <Chip 
              label="現在のプラン" 
              color={getPlanColor(currentPlan.id) as any}
              variant="outlined"
            />
          </Box>

          <Box display="flex" alignItems="baseline" gap={1} mb={3}>
            <Typography variant="h4" fontWeight="bold" color="primary.main">
              ¥{currentPlan.monthlyFee.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              / 月
            </Typography>
          </Box>

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowUpgradeDialog(true)}
              startIcon={<TrendingUpIcon />}
            >
              プラン変更
            </Button>
            <Tooltip title="Stripeカスタマーポータルで管理">
              <Button
                variant="outlined"
                onClick={async () => {
                  setIsLoadingPortal(true);
                  try {
                    await stripeService.redirectToCustomerPortal();
                  } catch (error) {
                    console.error('Failed to redirect to customer portal:', error);
                  } finally {
                    setIsLoadingPortal(false);
                  }
                }}
                disabled={isLoadingPortal}
                startIcon={<ManageIcon />}
              >
                {isLoadingPortal ? '読み込み中...' : 'サブスクリプション管理'}
              </Button>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* アラート表示 */}
      {activeAlerts.length > 0 && (
        <Box mb={3}>
          {activeAlerts.map(alert => (
            <Alert 
              key={alert.id}
              severity={alert.type === 'warning' ? 'warning' : 'error'}
              sx={{ mb: 1 }}
            >
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* 利用状況 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>今月の利用状況</Typography>
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
                {/* スタッフ使用率 */}
                {currentPlan.limits.staffCount > 0 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PeopleIcon fontSize="small" color="action" />
                        <Typography variant="body2">スタッフ数</Typography>
                      </Box>
                      <Typography variant="body2">
                        {usage.currentMonth.staffCount} / {currentPlan.limits.staffCount}人
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={usage.currentMonth.usage.staffUsage * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}

                {/* 予約数使用率 */}
                {currentPlan.limits.reservationsPerMonth !== 0 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2">月間予約数</Typography>
                      </Box>
                      <Typography variant="body2">
                        {usage.currentMonth.reservationCount} / {
                          isUnlimited(currentPlan.limits.reservationsPerMonth)
                            ? '無制限'
                            : `${currentPlan.limits.reservationsPerMonth}件`
                        }
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={
                        isUnlimited(currentPlan.limits.reservationsPerMonth)
                          ? 0
                          : usage.currentMonth.usage.reservationUsage * 100
                      }
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )}

                {/* AI分析使用率 */}
                {currentPlan.limits.aiAnalysisCount !== 0 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SmartToyIcon fontSize="small" color="action" />
                        <Typography variant="body2">AI分析</Typography>
                      </Box>
                      <Typography variant="body2">
                        {usage.currentMonth.aiAnalysisCount} / {
                          isUnlimited(currentPlan.limits.aiAnalysisCount)
                            ? '無制限'
                            : `${currentPlan.limits.aiAnalysisCount}回`
                        }
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={
                        isUnlimited(currentPlan.limits.aiAnalysisCount)
                          ? 0
                          : usage.currentMonth.usage.aiUsage * 100
                      }
                      sx={{ height: 6, borderRadius: 3 }}
                      color={
                        isUnlimited(currentPlan.limits.aiAnalysisCount)
                          ? 'primary'
                          : usage.currentMonth.usage.aiUsage > 0.9
                          ? 'error'
                          : usage.currentMonth.usage.aiUsage > 0.7
                          ? 'warning'
                          : 'primary'
                      }
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* プラン機能 */}
        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 400 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <CheckCircleIcon color="success" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>利用可能な機能</Typography>
              </Box>
              
              <List dense>
                {renderFeatures()}
              </List>

              {currentPlan.features.length > 5 && (
                <>
                  <Divider />
                  <Box textAlign="center" pt={2}>
                    <Typography variant="body2" color="text.secondary">
                      他 {currentPlan.features.length - 5} 個の機能
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 使用量インジケーター追加 */}
      <Card elevation={2} sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            使用量状況
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <UsageIndicator
              resource="スタッフ"
              icon={<PeopleIcon />}
              current={usage.currentMonth.staffCount}
              limit={currentPlan.limits.staffCount}
              unit="名"
              variant="detailed"
            />

            <UsageIndicator
              resource="月間予約"
              icon={<EventIcon />}
              current={usage.currentMonth.reservationCount}
              limit={currentPlan.limits.reservationsPerMonth}
              unit="件"
              variant="detailed"
            />

            <UsageIndicator
              resource="データ容量"
              icon={<StorageIcon />}
              current={Math.round(usage.currentMonth.storageUsedMB / 1024)}
              limit={currentPlan.limits.storageGB}
              unit="GB"
              variant="detailed"
            />

            {currentPlan.limits.aiAnalysisCount > 0 && (
              <UsageIndicator
                resource="AI分析"
                icon={<SmartToyIcon />}
                current={usage.currentMonth.aiAnalysisCount}
                limit={currentPlan.limits.aiAnalysisCount}
                unit="回"
                variant="detailed"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* アップグレードダイアログ */}
      <UpgradePrompt
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        currentPlan={currentPlan}
        recommendedPlan={'standard'}
      />
    </Box>
  );
};

export default PlanInfoCard;